<template>
  <default-layout disable-contact>
    <vue-headful
      :title="`${project && project.name} | ${unit && unit.name}`"
    />
    <v-layout
      v-if="units.length && currentUnit"
      ref="unitcard"
      class="unitcard ml-0 mr-0"
      row
      wrap
    >
      <back-button
        v-if="fabBackButtonVisible && $vuetify.breakpoint.mdAndUp"
        :move-back="false"
        @click="goToParentExterior"
      />
      <v-flex
        v-if="typesPanoramasLoaded"
        :class="floorPlans.length > 1 ? 'multiple-floorplans' : 'single-floorplan'"
        image_col
        lg9
        md8
        xs12
      >
        <template v-if="floorPlans.length > 1">
          <v-tabs v-model="activeTab" fixed-tabs>
            <v-tabs-items v-model="activeTab" :show-arrows="true">
              <v-tab-item
                v-for="(floorPlan, idx) in floorPlans"
                :key="idx"
                :class="{slide3D: floorPlan.type === '3D'}"
                :value="generateTabId(floorPlan.type, idx)"
                active-class="tab-active"
              >
                <UnitSingleImage
                  :crop="crop"
                  :root="$refs.unitcard"
                  :spots="floorPlan.spots"
                  :src="floorPlan.filepath"
                  :type="floorPlan.type"
                  :unit-type="currentUnitType"
                />
              </v-tab-item>
            </v-tabs-items>
            <v-tab
              v-for="(floorPlan, idx) in floorPlans"
              :key="idx"
              :href="`#${generateTabId(floorPlan.type, idx)}`"
            >
              <div class="thumbnail_image">
                <img :src="getCdn(`${floorPlan.filepath}?width=165&height=999`)"/>
              </div>
            </v-tab>
          </v-tabs>
          <div class="view_thumnails">
            <v-divider/>
            <div class="headings">
              <strong>{{ slideName }}</strong>
              &bull; {{ $t('Slide') }} {{ activeSlideIdx }} {{ $t('of') }} {{ floorPlans.length }}
            </div>
          </div>
        </template>
        <template v-else-if="floorPlans.length === 1">
          <UnitSingleImage
            :crop="crop"
            :root="$refs.unitcard"
            :spots="floorPlan.spots"
            :src="floorPlan.filepath"
            :type="floorPlan.type"
            :unit-type="currentUnitType"
            :external="floorPlan.external"
          />
        </template>
      </v-flex>
      <v-flex v-else image_col lg9 md8 xs12>
        <circle-spin :loading="true" class="turquoise--text" height="250" width="250"/>
      </v-flex>
      <v-flex info_col lg3 md4 xs12>
        <v-layout column>
          <div ref="carouselCover" class="flex carousel_cover order-xs3 order-md1">
            <template v-if="exterior">
              <svg
                v-if="exterior.overlay"
                ref="svg"
                :class="{ 'overlays-on': globalShowOverlayStatus }"
                class="exterior_container"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <image
                    :height="exterior.height"
                    :href="getCdn(exterior.filepath)"
                    :width="exterior.width"
                    x="0"
                    y="0"
                    @load="setAspectRation"
                  />
                  <viewport-overlay
                    v-for="overlay in overlays"
                    :key="overlay.overlay_id"
                    :class="{ active: exterior.overlay === overlay }"
                    :overlay="overlay"
                    :overlay-status="true"
                    :tooltip-disabled="true"
                    @toggleOverlay="goTo"
                  />
                </g>
              </svg>
              <img
                v-else
                :src="getCdn(`${exterior.filepath}?width=445&height=445`)"
              />
            </template>
          </div>
          <v-flex info_block order-md3 order-xs2>
            <div class="unit_title">
              {{ unit.name }}
              <span
                v-if="!!getUnitColorHex(unit)"
                :style="{'background-color': getUnitColorHex(unit)}"
                class="badge"
              >
                {{ $t(unit.status.name || unit.status.internal_slug) }}
              </span>
              <span
                v-else
                :class="['badge', unit.status.color_class || unit.status.color]"
              >
                {{ $t(unit.status.name || unit.status.internal_slug) }}
              </span>
              <span class="flex-grow-1 flex-shrink-1"></span>
              <button
                :aria-labelledby="$t('ADD_TO_FAVOURITES')"
                class="toggle-favourite-inline"
                role="button"
                @click="() => {isFavourite() ? removeFavourite() : addFavourite() }"
              >
                <i v-if="!isFavourite()" aria-hidden="true" class="v-icon material-icons">
                  favorite_border
                </i>
                <i v-if="isFavourite()" aria-hidden="true" class="v-icon material-icons">
                  favorite
                </i>
              </button>
            </div>
            <v-layout v-if="unit.status.display_attributes" class="options" row wrap>
              <v-flex v-for="(option, idx) in unitAttrData" :key="idx" md4 xs6>
                <div class="option_label">{{ option.label }}</div>
                <div class="option_value">{{ option.value }}</div>
              </v-flex>
            </v-layout>
            <v-container>
              <v-row class="options_footer">
                <v-btn v-if="showSiteLink" color="primary">Broker Site</v-btn>
                <v-col
                  class="col-md-4 col-xs-6 pa-0"
                  v-for="(file,idx) in unitDocuments"
                  :key="idx"
                >
                  <FileDownload
                    :filename="`attached_document`"
                    :format="file.type"
                    :size="file.size"
                    :src="file.external ? file.url : getCdn(file.url)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-flex>
        </v-layout>
        <div v-if="!!unitDescription" class="info_block">
          <div class="content_text_zone" v-html="unitDescription"></div>
        </div>
        <contact-form v-if="contactFormEnabledOnUnitPage"/>
      </v-flex>
    </v-layout>
    <v-layout v-else class="unitLoading fill-height">
      <v-row
        align="center"
        class="fill-height"
        justify="center"
      >
        <v-col md12 xs12>
          <circle-spin
            :loading="true"
            class="turquoise--text"
            height="250"
            width="250"
          />
        </v-col>
      </v-row>
    </v-layout>
  </default-layout>
</template>

<script>
import store from '@/store';
import { mapGetters, mapState } from 'vuex';
import DefaultLayout from '@/layouts/Default.vue';
import FileDownload from '@/components/FileDownload.vue';
import { cdnUrl } from '@/utils/urls';
import { attrKey2Label } from '@/utils/attribute-labels';
import { pricesAttributes } from '@/utils/attribute-prices';
import { CircleSpinner } from 'vue-spinners';
import VueHeadful from 'vue-headful';
import ExteriorNavigator from '@/mixins/ExteriorNavigator';
import UnitSingleImage from '@/components/UnitSingleImage.vue';
import ViewportOverlay from '@/components/ViewportOverlay.vue';
import ActiveExteriorChecker from '@/mixins/activeExteriorChecker';
import BackButton from '@/components/BackButton.vue';
import ContactForm from '@/components/ContactForm.vue';

export default {
  /**
   * The name of the page.
   */
  name: 'UnitSingle',

  mixins: [ExteriorNavigator, ActiveExteriorChecker],
  /**
   * The components that the page can use.
   */
  components: {
    ContactForm,
    BackButton,
    UnitSingleImage,
    DefaultLayout,
    FileDownload,
    CircleSpinner,
    VueHeadful,
    ViewportOverlay,
  },
  data() {
    return {
      activeTab: '',
      showSiteLink: false,
      favouritesList: [],
      typesPanoramasLoaded: false,
    };
  },
  computed: {
    ...mapGetters('units', ['unitById', 'currentUnit', 'favourites', 'units', 'crop']),
    ...mapState(['unitTypes']),
    ...mapGetters('unitTypes', ['unitTypeById', 'panoramaByUnitTypeId', 'currentUnitType']),
    ...mapGetters('project', [
      'styleDefault',
      'styles',
      'statuses',
      'attributes',
      'languageDefault',
      'settings',
      'project',
      'contactFormEnabledOnUnitPage',
      'showExternalFloorPlan',
      'showExternalPdf',
      'filterOutDisabledOverlays',
    ]),
    ...mapGetters('exteriors', ['exteriors', 'globalShowOverlayStatus']),
    ...mapGetters('panoramas', ['panoramas']),
    ...mapGetters('filters', ['filterOverlays']),
    ...mapGetters('service', ['getThemedVar', 'contactFormFieldsAllowed']),
    slideName() {
      return this.parseTabId(this.activeTab).type;
    },
    activeSlideIdx() {
      return this.parseTabId(this.activeTab).index + 1;
    },
    unit() {
      return this.unitById(parseInt(this.$route.params.unitId, 10));
    },
    unitAttrData() {
      const unitType = this.unitTypeById(this.currentUnit.unit_type_id);
      const attributesMeta = new Set(Object.keys(this.currentUnit.attributes)
        .concat(Object.keys(unitType.attributes))); // unique keys
      const formattedAttributes = Object.assign({}, this.currentUnit.formatted_attributes);
      const result = [];

      attributesMeta.forEach((key) => {
        if (this.isPriceAttributeHidden(key)) {
          return;
        }

        if (typeof formattedAttributes[key] !== 'undefined' && formattedAttributes[key] !== null) {
          result.push({
            label: this.$t(attrKey2Label[key]),
            value: formattedAttributes[key],
          });
        } else if (
          typeof unitType.formatted_attributes[key] !== 'undefined' && unitType.formatted_attributes[key] !== null
        ) {
          result.push({
            label: this.$t(attrKey2Label[key]),
            value: `${unitType.formatted_attributes[key]}`,
          });
        } else if (typeof unitType.attributes[key] !== 'undefined' && unitType.attributes[key] !== null) {
          result.push({
            label: this.$t(attrKey2Label[key]),
            value: `${unitType.attributes[key]}`,
          });
        }
      });

      return result;
    },
    unitDescription() {
      const unitType = this.unitTypeById(this.currentUnit.unit_type_id);

      return this.currentUnit.description || unitType.description;
    },
    unitDocuments() {
      if (this.showExternalPdf) {
        return !this.currentUnit.ext_fact_sheet_url ? [] : [
          {
            type: 'external link',
            size: null,
            url: this.currentUnit.ext_fact_sheet_url,
            external: true,
          },
        ];
      }
      return this.currentUnit.documents.length > 0
        ? this.currentUnit.documents
        : this.unitTypeById(this.currentUnit.unit_type_id).documents;
    },
    unitExteriors() {
      return !this.currentUnit
        ? []
        : this.exteriors
          .filter(exterior => this.currentUnit.exteriors.includes(exterior.exterior_id))
          .map(exterior => ({
            id: exterior.exterior_id,
            filepath: exterior.image,
            width: exterior.width,
            height: exterior.height,
            type: 'exterior',
            overlay: exterior.overlays.find(overlay => overlay.unit_id === this.currentUnit.unit_id),
            overlays: exterior.overlays,
          }));
    },
    exterior() {
      if (!this.currentUnit) {
        return {};
      }

      const exteriorId = +this.$route.params.exteriorId;
      const { interiors } = this.currentUnit;
      const items = [...this.unitExteriors, ...interiors];

      return items.find(({ id }) => id === exteriorId) || items[0];
    },
    overlays() {
      return this.filterOutDisabledOverlays(this.filterOverlays(this.exterior.overlays));
    },
    floorPlans() {
      if (!this.currentUnit) {
        return [];
      }

      if (this.showExternalFloorPlan) {
        const spots = this.getSpots();

        return [
          {
            spots,
            filepath: this.currentUnit.ext_floor_plan_url || '',
            type: '3D',
            external: true,
          }];
      }

      const unitType = this.unitTypeById(this.currentUnit.unit_type_id);
      const spots = this.getSpots();
      const interiors = this.currentUnit.interiors.concat(unitType.interiors);

      let spotsByFloorsCount = 0;
      const [less, more] = this.settings.unit_2d_first ? [-1, 1] : [1, -1];

      const floorPlans = Array
        .from(unitType.floorplans)
        .sort((fp1, fp2) => (fp1.type < fp2.type
          ? less
          : fp1.type > fp2.type
            ? more
            : 0
        ))
        .map((floorplan) => {
          const floorSpots = spots.filter(spot => spot.floor === floorplan.floor);
          if (floorSpots.length) {
            floorplan.spots = floorSpots;
            spotsByFloorsCount += floorSpots.length;
          }

          return {
            ...floorplan,
            caption: `Floor ${floorplan.floor} of ${unitType.attributes.floors_internal}`,
          };
        })
        .concat(interiors.map(interior => ({
          filepath: interior.images[0],
          type: this.$t('unit_slides_additional'),
          ...interior,
        })));

      if (!spotsByFloorsCount) {
        const fp3DFirst = floorPlans.find(fp => fp.type === '3D');
        if (fp3DFirst) {
          fp3DFirst.spots = spots;
        }
      }
      return floorPlans;
    },
    floorPlan() {
      return this.floorPlans[0];
    },
    fabBackButtonVisible() {
      return (window.history.length > 1); // > 2)
    },
  },
  watch: {
    $route(to) {
      if (to.params.unitId && this.units.length) {
        const currentUnit = this.unitById(parseInt(to.params.unitId, 10));
        this.typesPanoramasLoaded = false;

        store.dispatch('units/setCurrent', { unit: currentUnit })
          .then(() => {
            store.dispatch('unitTypes/loadPanorama', { unitTypeId: currentUnit.unit_type_id })
              .then(() => {
                this.getSpots();
                this.typesPanoramasLoaded = true;
                this.initActiveTab();
              });
          });
      }
    },
  },
  beforeMount() {
    const { iso: lang } = store.getters['project/languageDefault'];

    const loads = [
      [store.state.unitTypes.unitTypes, 'unitTypes/load'],
      [store.state.exteriors.exteriors, 'exteriors/load'],
      [store.state.filters.filters, 'filters/load'],
      [store.state.units.units, 'units/load'],
    ]
      .filter(([coll]) => !coll.length)
      .map(([, action]) => store.dispatch(action, { order: this.$route.params.order, lang }));

    Promise
      .all(loads)
      .then(() => store.dispatch('units/setCurrent', { unit: this.unit }))
      .then(() => store.dispatch('unitTypes/loadPanorama', { unitTypeId: this.unit.unit_type_id }))
      .then(() => {
        this.getSpots();
        this.typesPanoramasLoaded = true;
        this.initActiveTab();
      });
  },
  mounted() {
    this.initActiveTab();
  },
  methods: {
    initActiveTab() {
      if (this.floorPlans && this.floorPlans.length) {
        this.activeTab = this.generateTabId(this.floorPlans[0].type, 0);
      }
    },
    generateTabId(type, index) {
      return `slide-${type}-${index}`;
    },
    parseTabId(id = '') {
      const [, type = '', index = 0] = /^slide-(.*)-(\d+)$/.exec(id) || [];

      return { type, index: +index };
    },
    /* eslint-disable max-len */
    getSpots() {
      const panoramaData = this.currentUnit && this.panoramaByUnitTypeId(this.currentUnit.unit_type_id);

      return !panoramaData ? [] : panoramaData.map(spot => ({
        id: spot.id,
        panorama_id: spot.minimap_id,
        floor_id: +spot.floor_id,
        floor: 1 + (+spot.floor_id),
        url: spot.url || '',
        filepath: spot.url || '',
        position: {
          top: spot.y, // `${panoramaData.y * 100}%`,
          left: spot.x, // `${panoramaData.x * 100}%`,
        },
      }));
    },
    getCdn(source) {
      return cdnUrl(source);
    },
    isFavourite() {
      return this.favourites.some(fav => this.unit.unit_id === fav.unit_id);
    },
    addFavourite() {
      store.dispatch('units/addFavourite', { order: this.$route.params.order, unit: this.unit });
    },
    removeFavourite() {
      store.dispatch('units/removeFavourite', { order: this.$route.params.order, unit: this.unit });
    },
    isPriceAttributeHidden(key) {
      if (!pricesAttributes.includes(key)) {
        return;
      }
      const status = this.statuses.find(status => status.status_id === this.currentUnit.status.status_id);

      return !status.display_price;
    },
    goToParentExterior() {
      this.$router.go(-1);

      // const { exteriors: [exteriorId] } = this.currentUnit;
      // const exterior = this.exteriors.find(exterior => exterior.exterior_id === exteriorId);
      //
      // this.goToExterior(exterior);
    },
    getUnitColorHex(unit) {
      const overlayStatus = this.statuses.find(status => status.status_id === unit.status_id);
      return overlayStatus ? overlayStatus.color : null;
    },
    goTo(overlay) {
      const overlayStatus = this.statuses.find(status => overlay.status_id === status.status_id);

      if (!(overlayStatus && overlayStatus.accessible)) {
        return;
      }

      if (!this.checkExteriorStatusForOverlays(overlay)) {
        return;
      }

      if (overlay.link_active && !!overlay.url) {
        window.open(overlay.url, overlay.target);
        return;
      }

      overlay.linked_exterior_id
        ? this.$router.push({
          name: 'order.overview.exterior',
          params: { order: this.$route.params.order, exteriorId: overlay.linked_exterior_id },
        })
        : this.$router.push({
          name: 'order.unit.view',
          params: {
            order: this.$route.params.order,
            unitId: overlay.unit_id,
            exteriorId: this.$route.params.exteriorId || this.exterior.id,
          },
        });
    },
    setAspectRation(event) {
      const {
        width: { baseVal: { valueAsString: width } },
        height: { baseVal: { valueAsString: height } },
      } = event.target;

      this.$refs.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    },
  },
};
</script>
<style lang="scss">
.unitcard {
  position: relative;

  .info_col {
    box-shadow: var(--unit-single-info-col-box-shadow, 0 0 5px rgba(0, 0, 0, 0.3));
    background: #fff;
    height: var(--app-main-height);
    overflow: auto;
    overflow-x: hidden;
    position: relative;

    .carousel_cover {
      @supports (padding-left: env(safe-area-inset-left)) {
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
      }
    }

    @supports (padding-bottom: env(safe-area-inset-bottom)) {
      padding-bottom: calc(14px + env(safe-area-inset-bottom));
    }

    > div:last-of-type {
      border-bottom: none;
    }
  }

  .image_col {
    overflow: hidden;
    position: relative;
    @supports (padding-left: env(safe-area-inset-left)) {
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }
    .v-tabs--fixed-tabs>.v-tabs-bar .v-tab {
      width: initial;
      padding: 0 8px;
    }
    .v-tabs {
      height: var(--app-main-height);

      .v-window-item {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }

      .v-tabs__container {
        /*height: 159px;*/
        // padding-top: 41px;
      }

      .v-tabs-bar {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 114px;
      }

      .v-tabs__icon {
        &:before {
          background: #6ec2b3;
          color: #fff;
        }
      }

      .v-slide-group {
        .v-slide-group__content {
          .thumbnail_image {
            img {
              /*width: 85%;*/
              max-height: 84px;
            }
          }
        }
      }
    }
  }

  .view_thumnails {
    width: 100%;
    position: absolute;
    /*max-width: 800px;*/
    left: 0;
    bottom: 114px;
    /*transform: translateX(-50%);*/
    background: #fff;

    .headings {
      text-align: center;
      padding: 10px 0;
      color: rgba(55, 50, 50, 0.7);
    }
  }

  .v-tabs__div {
    height: 200 - 41px;

    .v-tabs__item {
      padding: 4px 6px;
    }

    .v-tabs__item--active {
      .thumbnail_image {
        box-shadow: 0 0 0 2px rgba(55, 50, 50, 0.7);
        transform: scale(1);
      }
    }

    .thumbnail_image {
      max-width: 165px;
      max-height: 110px;
      overflow: hidden;
      box-shadow: 0 0 0 1px rgba(55, 50, 50, 0.1);
      transform: scale(0.9);
      transition: all 300ms;

      img {
        width: 100%;
        display: block;
      }
    }
  }

  .info_headings {
    font-size: var(--unit-single-info-headings-font-size, 0.75em);
    padding: var(--unit-single-info-headings-padding, 16px 19px);
    margin: var(--unit-single-info-headings-margin, unset);
    height: var(--unit-single-info-headings-height, unset);
  }

  .info_block {
    padding: 16px 19px;

    &:not(:last-child) {
      border-bottom: var(--horizontal-dividing-line);
    }
  }

  .info_block,
  .info_headings {
    @supports (padding-left: env(safe-area-inset-left)) {
      padding-left: calc(19px + env(safe-area-inset-left));
      padding-right: calc(19px + env(safe-area-inset-right));
    }
  }

  .content_text_zone {
    font-size: 1em;

    p {
      white-space: pre-wrap;
    }

    a[href] {
      white-space: normal;
    }

    p:last-of-type {
      margin-bottom: 0;
    }
  }

  .unit_title {
    margin-bottom: 15px;
    font-size: 1.3em;
    display: flex;
    align-items: center;
    color: var(--unit-single-unit-title-color, rgba(55, 50, 50, 0.7));
  }

  .badge {
    color: #fff;
    padding: 1px 6px;
    border-radius: 2px;
    display: inline-block;
    font-size: 0.75em;
    margin-left: 12px;
  }

  .badge.color_status_a {
    background-color: rgba(var(--color-status-a), 1.0);
  }

  .badge.color_status_b {
    background-color: rgba(var(--color-status-b), 1.0);
  }

  .badge.color_status_c {
    background-color: rgba(var(--color-status-c), 1.0);
  }

  .badge.color_status_d {
    background-color: rgba(var(--color-status-d), 1.0);
  }

  .exterior_container {
    min-width: 100%;
    min-height: 150px;
  }

  .options {
    font-size: 1em;
    margin-left: 0;
    margin-right: 0;

    .flex {
      margin-bottom: 16px;
    }

    .option_label {
      color: var(--unit-single-unitcard-option, #6f6f6f);
    }

    .option_value {
      color: #000;
    }
  }

  .options_footer {
    margin-bottom: -15px;

    .v-btn, a {
      margin: 0;
      margin-right: 7%;
      margin-bottom: 15px;
    }
  }

  .v-form {
    .v-btn {
      margin: 0;
      margin-top: 14px;
      width: 200px;
      max-width: 100%;
    }
  }

  .checkbox_units_to_attach {
    padding-left: 32px;
    //margin-top: -20px;
    font-size: 1em;
    color: rgba(0, 0, 0, 0.54);
  }

  path, polygon {
    &.active {
      fill: rgba(55, 218, 253, var(--hover-fill-opacity)) !important;
      stroke: #37dafd !important;
    }

    user-select: none;
    pointer-events: visible;
    stroke-width: 4px;
    stroke-linecap: round;
    transition: all .2s ease;
    outline: none;
  }

  .v-btn.toggle-favourite {
    position: fixed;
    z-index: 20;

    bottom: 20px;
    right: 100px;
    border-radius: 28px;
    width: 56px;
    height: 56px;
  }

  .toggle-favourite-inline {
    display: var(--unit-single-favourite-toggle-display, block);

    span {
      text-decoration: none;
      text-transform: uppercase;
      margin-left: 6px;
    }
  }

  .overlay-status-switch {
    position: fixed;
    bottom: 20px;
    left: 25px;
    border-radius: 28px;
  }

  .fab-back-button {
    position: fixed;
    z-index: 20;

    top: var(--app-main-top);
    left: 15px;
    border-radius: 28px;
    width: 56px;
    height: 56px;
  }

  .single-floorplan .view-cover {
    height: var(--app-main-height);
  }

  .multiple-floorplans .view-cover {
    height: calc(100vh - 156px - var(--app-bar-height));
  }
}

@media screen and (max-width: 960px) {
  .unitcard {
    .options_footer {
      a {
        max-width: 100%;
      }
    }

    .view_thumnails {
      /*bottom: auto;*/
      /*top: 200px;*/
      display: none;
    }

    .image_col {
      .v-tabs {
        height: auto;
        display: flex;
        flex-direction: column;

        .v-tabs__bar {
          position: relative;
          margin-top: 41px;
          order: 2;
        }

        .v-window {
          order: 1;
        }

        .v-slide-group {
          display: none;
        }
      }

      .v-window__container {
        height: 400px;
      }

      .view-cover {
        height: 400px;
        text-align: center;
      }
    }

    .info_col {
      height: auto;
      overflow: visible;
    }
  }
}

@media screen and (min-width: 961px) {
  .unitcard {
    .image_col {
      .v-tabs {
        .v-window__prev, .v-window__next {
          display: none;
        }
      }
    }
  }
}

.carousel_cover {
  svg {
    &:not(.overlays-on) {
      .display-status:not(.active) {
        --fill-opacity: 0;
        --stroke-opacity: 0;
      }

      .display-status.active {
        --fill-opacity: var(--hover-fill-opacity);
        --stroke-opacity: var(--hover-stroke-opacity);
      }
    }
  }
}
</style>
